import {
  format,
  parseISO,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Labelled } from "@rentiohq/web-shared/dist/components";
import { BaseDatePicker } from "@rentiohq/web-shared/dist/components/BaseDatePicker";
import React from "react";

const ISO_DAY_FORMAT = "yyyy-MM-dd";
const DISPLAY_FORMAT = "dd/MM/yyyy";

interface IIsoDateFieldProps {
  id: string;
  label: string;
  // "yyyy-MM-dd", or "" while empty: the format the costs feature stores.
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  maxDate?: string;
  error?: string;
  disabled?: boolean;
}

// Both conversions stay in local time: going through toISOString() shifts the
// day for anyone east of UTC.
const toDate = (value?: string): Date | undefined =>
  value ? parseISO(value) : undefined;

const toIsoDay = (date: Date): string => format(date, ISO_DAY_FORMAT);

// A day picker that follows the Rentio language. The browser's own date input
// shows its placeholder and calendar in the browser's language, so a French
// Rentio user on a Dutch browser got a Dutch placeholder. The calendar is
// portaled, so the scrolling body of a modal does not clip it.
export const IsoDateField = (props: IIsoDateFieldProps) => {
  const { id, label, value, onChange, minDate, maxDate, error, disabled } =
    props;

  const pickerRef = React.useRef<React.ElementRef<typeof BaseDatePicker>>(null);

  // Event handlers
  const handleChange = (date: Date | null) => {
    onChange(date ? toIsoDay(date) : "");
  };

  const handleClear = () => {
    onChange("");
  };

  const handleIconClick = () => {
    pickerRef.current?.setOpen(true);
  };

  // Render
  return (
    <Labelled label={label} error={error}>
      <BaseDatePicker
        ref={pickerRef}
        id={id}
        selected={toDate(value)}
        minDate={toDate(minDate)}
        maxDate={toDate(maxDate)}
        onChange={handleChange}
        placeholderText={getLocalizedText(`date.placeholder.${DISPLAY_FORMAT}`)}
        dateFormat={DISPLAY_FORMAT}
        hideDayPicker={false}
        hasError={!!error}
        isDisabled={!!disabled}
        required={true}
        inverseYears={false}
        handleClear={handleClear}
        handleIconClick={handleIconClick}
      />
    </Labelled>
  );
};
